import { useEffect, useRef, useState } from "preact/hooks";
import {
  type CardVariant,
  fetchCardVariants,
  getCachedCardVariants,
} from "../api/cards";
import {
  colorLabel,
  colorOf,
  fabraryUrl,
  rarityLabel,
} from "../lib/cardFormat";
import { FabText } from "./FabText";

/** Popup dimensions, kept in sync with `RuleRef`. */
const PANEL_WIDTH = 352;
const EDGE = 8;

/** CSS modifier for a rarity code, e.g. `C` -> `rarity-mark rarity-mark--c`. */
export function rarityMarkClass(code: string | null): string {
  return `rarity-mark rarity-mark--${(code ?? "").toLowerCase()}`;
}

/**
 * Presentational popup body, shared by `CardRef` and the dev preview page.
 * Renders the title, stats (including the colored rarity mark), text, variant
 * dots, and footer. Positioning and sizing are owned by the caller.
 */
export function CardPopup({
  name,
  variants,
  index,
  error = null,
  onSelect,
}: {
  name: string;
  variants: CardVariant[] | null;
  index: number;
  error?: string | null;
  onSelect?: (index: number) => void;
}) {
  // Mouse-only hover (see `.card-ref__dots--hover`): touch taps must not morph
  // the selector, so track the pointer type explicitly.
  const [hovering, setHovering] = useState(false);
  const count = variants?.length ?? 0;
  const active = variants?.[index] ?? null;

  const stats = active
    ? [
      active.cost && `Cost ${active.cost}`,
      active.power && `Power ${active.power}`,
      active.defense && `Defense ${active.defense}`,
    ].filter((stat): stat is string => Boolean(stat))
    : [];
  const rarity = active ? rarityLabel(active.rarity) : null;

  return (
    <>
      <span class="rule-ref__title">{name}</span>

      {variants === null && !error && (
        <span class="rule-ref__text">Loading…</span>
      )}
      {error && (
        <span class="rule-ref__error" role="alert">
          {error}
        </span>
      )}
      {variants !== null && count === 0 && !error && (
        <span class="rule-ref__text">No card text found.</span>
      )}

      {active && (
        <>
          {active.typeText && (
            <span class="card-ref__type">{active.typeText}</span>
          )}
          {(stats.length > 0 || rarity) && (
            <span class="card-ref__stats">
              {rarity && (
                <span class="card-ref__stat card-ref__rarity">
                  <span
                    class={rarityMarkClass(active.rarity)}
                    aria-hidden="true"
                  />
                  {rarity}
                </span>
              )}
              {stats.map((stat) => (
                <span class="card-ref__stat" key={stat}>
                  {stat}
                </span>
              ))}
            </span>
          )}
          <span class="rule-ref__text">
            <FabText text={active.text ?? "(no functional text)"} />
          </span>
          {count > 1 && (
            <span
              class={`card-ref__dots${
                hovering ? " card-ref__dots--hover" : ""
              }`}
              onPointerEnter={(event) => {
                if (event.pointerType === "mouse") setHovering(true);
              }}
              onPointerLeave={() => setHovering(false)}
            >
              {variants!.map((variant, i) => (
                <button
                  type="button"
                  key={variant.uniqueId}
                  class={[
                    "pitch-dot",
                    `pitch-dot--${colorOf(variant)}`,
                    i === index ? "pitch-dot--active" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  aria-label={colorLabel(variant)}
                  aria-current={i === index}
                  onClick={() => onSelect?.(i)}
                />
              ))}
            </span>
          )}
          <span class="card-ref__meta">
            <span class="card-ref__swatch-label">
              <span
                class={`pitch-dot pitch-dot--${colorOf(active)}`}
                aria-hidden="true"
              />
              {colorLabel(active)}
            </span>
            <a
              class="rule-ref__link"
              href={fabraryUrl(active)}
              target="_blank"
              rel="noreferrer"
            >
              View on Fabrary ↗
            </a>
          </span>
        </>
      )}
    </>
  );
}

/**
 * Inline card reference (e.g. `[Sink Below]`), shown for cards the model
 * explicitly cited. Opens a small popover with the card's text; variants can be
 * flipped with the dots, backed by the per-session card cache.
 *
 * The judge model only brackets cards it relies on, so this stays precise even
 * when the answer mentions a card in passing without citing it.
 */
export function CardRef({
  name,
  previewVariants,
  defaultOpen = false,
}: {
  name: string;
  /** Sample variants for dev previews; skips the DB lookup when provided. */
  previewVariants?: CardVariant[];
  defaultOpen?: boolean;
}) {
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(defaultOpen);
  const [left, setLeft] = useState<number | null>(null);
  const [above, setAbove] = useState(false);
  const [variants, setVariants] = useState<CardVariant[] | null>(() =>
    previewVariants ?? getCachedCardVariants(name)
  );
  const [error, setError] = useState<string | null>(null);
  const [index, setIndex] = useState(0);
  const swipeStart = useRef<number | null>(null);

  function reposition() {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const rect = wrapper.getBoundingClientRect();
    const width = Math.min(PANEL_WIDTH, window.innerWidth - EDGE * 2);
    const desired = rect.left + rect.width / 2 - width / 2;
    const clamped = Math.max(
      EDGE,
      Math.min(desired, window.innerWidth - width - EDGE),
    );
    setLeft(clamped - rect.left);

    // Flip above the reference when there is not enough room below.
    const spaceBelow = window.innerHeight - rect.bottom;
    setAbove(spaceBelow < 260 && rect.top > spaceBelow);
  }

  function toggle() {
    if (open) {
      setOpen(false);
      return;
    }

    if (variants === null && error === null) {
      const cached = getCachedCardVariants(name);
      if (cached) {
        setVariants(cached);
      } else {
        fetchCardVariants(name)
          .then((rows) => setVariants(rows))
          .catch((cause: unknown) =>
            setError(
              cause instanceof Error ? cause.message : "Could not load card.",
            )
          );
      }
    }
    setOpen(true);
  }

  useEffect(() => {
    if (!open) return;

    reposition();
    const onResize = () => reposition();
    function onPointerDown(event: MouseEvent) {
      const wrapper = wrapperRef.current;
      if (wrapper && !wrapper.contains(event.target as Node)) setOpen(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    window.addEventListener("resize", onResize);
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("resize", onResize);
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const count = variants?.length ?? 0;

  function step(delta: number) {
    if (count < 2) return;
    setIndex((current) => (current + delta + count) % count);
  }

  return (
    <span class="rule-ref card-ref" ref={wrapperRef}>
      <button
        type="button"
        class="rule-ref__btn card-ref__btn"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={toggle}
      >
        {name}
      </button>

      {open && (
        <span
          class="rule-ref__panel card-ref__panel"
          role="dialog"
          aria-label={`${name} card`}
          data-swipe-ignore
          onTouchStart={(event) => {
            swipeStart.current = event.touches[0]?.clientX ?? null;
          }}
          onTouchEnd={(event) => {
            const start = swipeStart.current;
            swipeStart.current = null;
            if (start === null) return;
            const end = event.changedTouches[0]?.clientX ?? start;
            const delta = end - start;
            if (Math.abs(delta) > 40) step(delta < 0 ? 1 : -1);
          }}
          style={{
            width: `min(22rem, calc(100vw - ${EDGE * 2}px))`,
            left: `${left ?? 0}px`,
            top: above ? "auto" : "calc(100% + 6px)",
            bottom: above ? "calc(100% + 6px)" : "auto",
            visibility: left === null ? "hidden" : "visible",
          }}
        >
          <CardPopup
            name={name}
            variants={variants}
            index={index}
            error={error}
            onSelect={setIndex}
          />
        </span>
      )}
    </span>
  );
}
